import React, { useState } from 'react';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Switch,
  Alert,
} from '@cargomatic/ui';
import {
  billingStatusList,
  getBillingStatusLabel,
} from 'constants/billing-status';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { overrideBillingStatus } from 'utils/ShipmentWebAPIUtils';
import { bulkOverrideOrderBillingStatus } from 'utils/OrderWebAPIUtils';
import SessionStore from 'stores/SessionStore';

export function OverrideBillingStatusModal({
  shipmentIds = [],
  orderIds = [],
  orderId = null,
  open = false,
  onHide,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [billingStatus, setBillingStatus] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [pauseAutomatedEvaluations, setPauseAutomatedEvaluations] =
    useState(true);

  const [errors, setErrors] = useState({});

  function validate() {
    const errors = {};
    if (!billingStatus) {
      errors.billingStatus = `${t('BILLING_STATUS')} ${t('REQUIRED')}`;
    }
    if (!reasonCode && !SessionStore.isBillingStatusAdmin()) {
      errors.reasonCode = `${t('REASON_CODE')} ${t('REQUIRED')}`;
    }
    setErrors(errors);
    return errors;
  }

  async function handleSave() {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      if (orderId) {
        const payload = {
          billing_status: billingStatus,
          pause_automated_billing_evaluation: pauseAutomatedEvaluations,
          pause_automated_billing_evaluation_reason_code: reasonCode,
        };
        await overrideBillingStatus(payload, orderId);
      } else if (shipmentIds.length > 0) {
        const payload = shipmentIds.map((shipmentId) => ({
          _id: shipmentId,
          billing_status: billingStatus,
          pause_automated_billing_evaluation: pauseAutomatedEvaluations,
          pause_automated_billing_evaluation_reason_code: reasonCode,
        }));
        await overrideBillingStatus(payload);
      } else if (orderIds.length > 0) {
        const operations = orderIds.map((orderId) => ({
          order_id: orderId,
          billing_status: billingStatus,
          pause_automated_billing_evaluation: pauseAutomatedEvaluations,
          pause_automated_billing_evaluation_reason_code: reasonCode,
        }));
        console.log(
          'OverrideBillingStatusModal calling bulkOverrideOrderBillingStatus',
          operations,
        );
        await bulkOverrideOrderBillingStatus(operations);
      }

      showToast(`${t('BILLING_STATUS')} ${t('UPDATED')}`);
      onHide();
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      showToast(`${t('BILLING_STATUS')} ${t('ERROR')}`);
      console.error(e);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onHide}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('OVERRIDE_READY_TO_BILL_STATUS')}</DialogTitle>
          <DialogDescription>
            {t('MANUAL_OVERRIDE_READY_TO_BILL_STATUS')}
          </DialogDescription>
        </DialogHeader>
        {Object.values(errors).length > 0 && (
          <Alert>{t('FILL_ALL_REQUIRED_FIELDS')} (*)</Alert>
        )}
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="billing-status" className="">
              {t('BILLING_STATUS')} *
            </Label>
            <Select value={billingStatus} onValueChange={setBillingStatus}>
              <SelectTrigger className="">
                <SelectValue
                  placeholder={`${t('SELECT')} ${t('BILLING_STATUS')} `}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('BILLING_STATUS')}</SelectLabel>
                  {billingStatusList.map((status) => (
                    <SelectItem value={status} key={status}>
                      {getBillingStatusLabel(status, t)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reason-code" className="">
              {t('REASON_CODE')} *
            </Label>
            <Input
              id="reason-code"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              placeholder="Provide a reason"
              className="w-full"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="pause-automated-evaluations"
              checked={pauseAutomatedEvaluations}
              onCheckedChange={setPauseAutomatedEvaluations}
            />
            <Label htmlFor="pause-automated-evaluations">
              {t('PAUSE_AUTOMATED_EVALUATIONS')}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleSave}>
            {t('SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
